import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  X,
  LogOut,
  Baby,
  Phone,
  MapPin,
  Edit2
} from 'lucide-react';
import { useViewOnly } from '../contexts/ViewOnlyContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { PhoneInput } from './ui/phone-input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Sidebar = ({ isOpen, onClose, baby }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewOnlyMode, disableViewOnlyMode } = useViewOnly();
  const isSyncedDevice = localStorage.getItem('isSyncedDevice') === 'true';
  const [showEmergencyContactDialog, setShowEmergencyContactDialog] = useState(false);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [emergencyContactData, setEmergencyContactData] = useState({
    emergency_contact_phone: baby?.emergency_contact_phone || '',
    country_code: '+91'
  });
  const [addressData, setAddressData] = useState({
    address: baby?.address || ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Helper function to normalize gender display
  const normalizeGender = (gender) => {
    if (!gender) return 'Unknown';
    const lowerGender = gender.toLowerCase();
    if (lowerGender === 'boy' || lowerGender === 'male' || lowerGender === 'm') return 'Male';
    if (lowerGender === 'girl' || lowerGender === 'female' || lowerGender === 'f') return 'Female';
    return gender;
  };

  const navigationItems = [];

  const handleSaveEmergencyContact = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/babies/${baby.id}`, emergencyContactData);
      
      // Update localStorage with new emergency contact
      const currentBaby = JSON.parse(localStorage.getItem('currentBaby') || '{}');
      currentBaby.emergency_contact_phone = emergencyContactData.emergency_contact_phone;
      localStorage.setItem('currentBaby', JSON.stringify(currentBaby));
      
      // Also update 'baby' key if it exists
      const babyData = localStorage.getItem('baby');
      if (babyData) {
        const parsedBaby = JSON.parse(babyData);
        parsedBaby.emergency_contact_phone = emergencyContactData.emergency_contact_phone;
        localStorage.setItem('baby', JSON.stringify(parsedBaby));
      }
      
      toast.success('Emergency contact updated successfully!');
      setShowEmergencyContactDialog(false);
      
      // Update baby object directly instead of reloading
      baby.emergency_contact_phone = emergencyContactData.emergency_contact_phone;
    } catch (error) {
      console.error('Error updating emergency contact:', error);
      toast.error('Failed to update emergency contact');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/babies/${baby.id}`, addressData);
      
      // Update localStorage with new address
      const currentBaby = JSON.parse(localStorage.getItem('currentBaby') || '{}');
      currentBaby.address = addressData.address;
      localStorage.setItem('currentBaby', JSON.stringify(currentBaby));
      
      // Also update 'baby' key if it exists
      const babyData = localStorage.getItem('baby');
      if (babyData) {
        const parsedBaby = JSON.parse(babyData);
        parsedBaby.address = addressData.address;
        localStorage.setItem('baby', JSON.stringify(parsedBaby));
      }
      
      toast.success('Address updated successfully!');
      setShowAddressDialog(false);
      
      // Update baby object directly instead of reloading
      baby.address = addressData.address;
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error('Failed to update address');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    if (viewOnlyMode) {
      // Close view-only mode and return to main baby
      disableViewOnlyMode();
      sessionStorage.removeItem('viewOnlyBaby');
      // Reload to show the main baby from localStorage
      window.location.href = '/';
    } else {
      // Logout from main app
      localStorage.removeItem('currentBaby');
      localStorage.removeItem('baby');
      sessionStorage.clear();
      // Force full page reload to clear all state
      window.location.href = '/login';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
              <Baby className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900" style={{fontFamily: 'Space Grotesk'}}>
                My Health Record
              </h2>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Baby Profile */}
        {baby && (
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-lg">
                  {baby.name ? baby.name.charAt(0).toUpperCase() : 'B'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate" style={{fontFamily: 'Space Grotesk'}}>
                  {baby.name ? baby.name.charAt(0).toUpperCase() + baby.name.slice(1) : 'Baby'}
                </h3>
                <p className="text-sm text-gray-600">
                  {normalizeGender(baby.gender)} • ID: {baby.short_id || baby.id.slice(0, 8).toUpperCase()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6">
          {/* Emergency Contact Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Emergency Contact</h3>
              {!viewOnlyMode && !isSyncedDevice && (
                <button
                  onClick={() => {
                    setEmergencyContactData({
                      emergency_contact_phone: baby?.emergency_contact_phone || '',
                      country_code: '+91'
                    });
                    setShowEmergencyContactDialog(true);
                  }}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {baby?.emergency_contact_phone ? (
              <div className="bg-red-50 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Phone className="w-4 h-4 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Emergency Contact</p>
                    <p className="text-sm text-gray-700 mt-1">{baby.emergency_contact_phone}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-sm text-gray-500 text-center">No emergency contact added</p>
              </div>
            )}
          </div>
          
          {/* Address Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Address</h3>
              {!viewOnlyMode && !isSyncedDevice && (
                <button
                  onClick={() => {
                    setAddressData({
                      address: baby?.address || ''
                    });
                    setShowAddressDialog(true);
                  }}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {baby?.address ? (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700 flex-1">{baby.address}</p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500 text-center">No address added</p>
              </div>
            )}
          </div>
        </nav>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 p-4">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
              viewOnlyMode 
                ? 'bg-red-500 hover:bg-red-600 text-white' 
                : 'bg-red-500 hover:bg-red-600 text-white'
            }`}
            data-testid="sidebar-logout"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">
              {viewOnlyMode ? 'Close Viewing' : 'Logout'}
            </span>
          </button>
        </div>
      </div>

      {/* Emergency Contact Dialog */}
      <Dialog open={showEmergencyContactDialog} onOpenChange={setShowEmergencyContactDialog}>
        <DialogContent className="sm:max-w-md z-[60]">
          <DialogHeader>
            <DialogTitle>Emergency Contact</DialogTitle>
            <DialogDescription>
              Add or update emergency contact information for this baby
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEmergencyContact} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="emergency_phone">Emergency Phone Number *</Label>
              <PhoneInput
                value={emergencyContactData.emergency_contact_phone}
                onChange={(value) => setEmergencyContactData(prev => ({ ...prev, emergency_contact_phone: value }))}
                countryCode={emergencyContactData.country_code}
                onCountryCodeChange={(code) => setEmergencyContactData(prev => ({ ...prev, country_code: code }))}
                placeholder="Enter 10-digit mobile number"
                required
              />
              <p className="text-xs text-gray-500">This number will be used in case of emergencies</p>
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowEmergencyContactDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Address Dialog */}
      <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
        <DialogContent className="sm:max-w-md z-[60]">
          <DialogHeader>
            <DialogTitle>Address</DialogTitle>
            <DialogDescription>
              Add or update residential address for this baby
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveAddress} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address">Full Address</Label>
              <Textarea
                id="address"
                placeholder="Enter complete address"
                value={addressData.address}
                onChange={(e) => setAddressData(prev => ({ ...prev, address: e.target.value }))}
                rows={4}
              />
            </div>
            
            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1"
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save'}
              </Button>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => setShowAddressDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sidebar;